import React from "react";
import { Box, Paper, Stack, Typography, Zoom, useTheme } from "@mui/material";
import EventIcon from "@mui/icons-material/Event";

import { getHomeRegistrationHeroPaperSx } from "./homeConstants";
import { HomeKeyFactsSection } from "./HomeKeyFactsSection";
import { useAppSelector } from "../../store";
import { getTournoi } from "../../features/tournoi/tournoiSlice";
import { resolveTournoiHome } from "../../utils/resolveTournoiHome";

export type HomeOpenRegistrationHeroProps = {
  tournamentLabel: string;
  /**
   * Libellé de date tel qu’affiché (ex. « 27 juin 2026 »).
   * Source : champ Firestore `dateFinInscriptions` du tournoi ; repli côté `Home` si vide.
   */
  dateFinInscriptions: string;
  lieuDisplay: string | null;
};

/**
 * Hero compact « inscriptions ouvertes » : même esprit que le haut de la home pré-ouverture,
 * avec date de fin d’inscription puis bloc compétition / lieu / dotations.
 */
export const HomeOpenRegistrationHero: React.FC<
  HomeOpenRegistrationHeroProps
> = ({ tournamentLabel, dateFinInscriptions, lieuDisplay }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tournoi = useAppSelector(getTournoi);
  const home = resolveTournoiHome(tournoi);
  const lieuLine =
    lieuDisplay?.trim() ||
    "Gymnase des Droits de l’Homme, Guyancourt (78)";

  return (
    <Zoom in>
      <Paper elevation={0} sx={getHomeRegistrationHeroPaperSx(theme)}>
        <Stack spacing={{ xs: 2.25, sm: 2.5 }}>
          <Box sx={{ textAlign: "center", px: { xs: 0.5, sm: 1 } }}>
            <Typography
              variant="overline"
              sx={{
                display: "block",
                fontWeight: 700,
                letterSpacing: "0.16em",
                color: isDark
                  ? theme.palette.grey[200]
                  : theme.palette.primary.dark,
                mb: 1,
              }}
            >
              {tournamentLabel}
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{ mb: 0.5 }}
            >
              <EventIcon
                sx={{
                  fontSize: 30,
                  color: isDark
                    ? theme.palette.grey[200]
                    : theme.palette.primary.main,
                }}
              />
              <Typography
                variant="h6"
                component="h1"
                sx={{
                  fontWeight: 800,
                  letterSpacing: "-0.02em",
                  color: "text.primary",
                }}
              >
                Inscriptions en ligne
              </Typography>
            </Stack>
            <Typography
              variant="h4"
              component="p"
              sx={{
                m: 0,
                mt: 0.5,
                fontWeight: 800,
                color: isDark
                  ? theme.palette.secondary.light
                  : theme.palette.primary.dark,
                fontSize: { xs: "1.5rem", sm: "1.85rem" },
                lineHeight: 1.3,
              }}
            >
              Jusqu&apos;au {dateFinInscriptions}
            </Typography>
          </Box>

          <HomeKeyFactsSection
            lieuLine={lieuLine}
            weekendLine={home.weekendLine}
            dotationsLine={home.dotationsLine}
          />
        </Stack>
      </Paper>
    </Zoom>
  );
};
